import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FileText, RefreshCw, ExternalLink, AlertCircle } from 'lucide-react';

const PDFViewer = ({ pdfUrl, title }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const iframeRef = useRef(null);
  const loadTimeoutRef = useRef(null);
  const checkIntervalRef = useRef(null);

  // Google Docs Viewer URL
  const googleViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(pdfUrl)}&embedded=true`;
  
  // Microsoft Office Viewer as fallback
  const msViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(pdfUrl)}`;

  // Choose viewer based on retry count
  const getViewerUrl = useCallback(() => {
    // Alternate between Google and Microsoft viewers on retries
    if (retryCount % 2 === 0) {
      return googleViewerUrl;
    }
    return msViewerUrl;
  }, [retryCount, googleViewerUrl, msViewerUrl]);

  useEffect(() => {
    // Reset state when PDF changes
    setIsLoading(true);
    setLoadFailed(false);
    setRetryCount(0);

    // Clear any existing timeouts/intervals
    if (loadTimeoutRef.current) clearTimeout(loadTimeoutRef.current);
    if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);

    // Set loading timeout - if still loading after 8 seconds, show content anyway
    loadTimeoutRef.current = setTimeout(() => {
      setIsLoading(false);
    }, 8000);

    // Check iframe periodically to detect if it loaded
    let checkCount = 0;
    checkIntervalRef.current = setInterval(() => {
      checkCount++;
      if (checkCount >= 4) {
        // After 4 seconds, assume it's loaded or loading
        setIsLoading(false);
        clearInterval(checkIntervalRef.current);
      }
    }, 1000);

    return () => {
      if (loadTimeoutRef.current) clearTimeout(loadTimeoutRef.current);
      if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
    };
  }, [pdfUrl]);

  const handleRetry = () => {
    setIsLoading(true);
    setLoadFailed(false);
    setRetryCount(prev => prev + 1);
    
    // Force iframe reload with new URL
    if (iframeRef.current) {
      const newUrl = retryCount % 2 === 0 ? msViewerUrl : googleViewerUrl;
      iframeRef.current.src = newUrl;
    }
    
    loadTimeoutRef.current = setTimeout(() => {
      setIsLoading(false);
    }, 8000);
  };

  const handleIframeLoad = () => {
    // Clear timeouts when iframe loads
    if (loadTimeoutRef.current) clearTimeout(loadTimeoutRef.current);
    if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
    setIsLoading(false);
  };

  return (
    <div className="pdf-container relative">
      {/* Loading Overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-card/90 backdrop-blur-sm flex flex-col items-center justify-center z-10 rounded-2xl border border-border">
          <div className="w-16 h-16 rounded-2xl bg-secondary flex items-center justify-center mb-4">
            <FileText className="w-8 h-8 text-primary animate-pulse" />
          </div>
          <p className="text-foreground font-medium mb-2">Loading PDF...</p>
          <p className="text-sm text-muted-foreground mb-4">This may take a few seconds</p>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
            <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
            <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
          </div>
        </div>
      )}

      {/* Error/Fallback State */}
      {loadFailed && !isLoading && (
        <div className="absolute inset-0 bg-card flex flex-col items-center justify-center z-10 rounded-2xl border border-border">
          <div className="w-16 h-16 rounded-2xl bg-secondary flex items-center justify-center mb-4">
            <AlertCircle className="w-8 h-8 text-muted-foreground" />
          </div>
          <p className="text-foreground font-medium mb-2">Unable to load PDF preview</p>
          <p className="text-sm text-muted-foreground mb-6 text-center max-w-md px-4">
            The PDF viewer is having trouble loading. You can try again or open the PDF directly.
          </p>
          <div className="flex gap-3">
            <button
              onClick={handleRetry}
              className="btn-secondary px-5 py-2.5 rounded-xl text-sm font-semibold inline-flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Try Again
            </button>
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary px-5 py-2.5 rounded-xl text-sm font-semibold inline-flex items-center gap-2"
            >
              <ExternalLink className="h-4 w-4" />
              Open PDF
            </a>
          </div>
        </div>
      )}

      {/* PDF Iframe */}
      <iframe
        ref={iframeRef}
        src={getViewerUrl()}
        className="w-full h-[600px] lg:h-[800px] rounded-2xl bg-white"
        title={title}
        style={{ border: 'none' }}
        onLoad={handleIframeLoad}
      />

      {/* Quick action bar below PDF */}
      {!isLoading && !loadFailed && (
        <div className="mt-4 flex items-center justify-center gap-4 text-sm">
          <span className="text-muted-foreground">PDF not loading?</span>
          <button
            onClick={handleRetry}
            className="text-primary hover:text-primary/80 transition-smooth inline-flex items-center gap-1"
          >
            <RefreshCw className="h-3 w-3" />
            Reload
          </button>
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:text-primary/80 transition-smooth inline-flex items-center gap-1"
          >
            <ExternalLink className="h-3 w-3" />
            Open in new tab
          </a>
        </div>
      )}
    </div>
  );
};

export default PDFViewer;
